import React, { useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Colors, FontSizes, FontWeights } from 'constants/Clementine';
import Moment from 'moment';
import OpenUrlModal from './OpenUrlModal';
import FormUtils from 'utils/Form';
import PostToUrlModal from './PostToUrlModal';
import FirelightEappModal from './FirelightEappModal';
import FirelightEticketModal from './FirelightEticketModal';
import Loader from '../shared/Loader';
import { ProductContext } from './Context';
import AnnuitiesApi from '../../utils/AnnuitiesApi';
import RequestFormsModal from './RequestFormsModal';
import { Modal } from '@hexure/ui';
import PdfActions from '../shared/PdfActions';
import AaqApi from 'utils/AnnuitiesApi';

const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  padding: 40px;
`;

const QUOTER_CONFIG = window.HEXURE_AQ_CONFIG;
const FL_DISABLED_ACTION_TYPES = ['applicant', 'firelight', 'psg_eapp'];

const baseStyles = {
  content: { padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  wrapper: { display: 'flex', alignItems: 'center', justifyContent: 'center', flexFlow: 'wrap' },
  box_button: {
    width: 120,
    height: 120,
    margin: '0 10px 20px',
    padding: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    border: '1px solid #ccc',
    borderRadius: 4,
    cursor: 'pointer'
  },
  box_button_selected: { border: `2px solid ${Colors.GREEN.hex}` },
  box_button_image: { height: 40, width: 40, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center' },
  box_button_icon: { fontSize: 40 },
  box_button_label: { marginTop: 10, fontSize: FontSizes.MEDIUM, fontWeight: FontWeights.MEDIUM }
};

const META_DATA = {
  default: { icon: 'mdi-open-in-new', modal: 'open_url' },
  default_modal: { icon: 'mdi-link', modal: 'open_url' },
  webhook_post: { icon: 'mdi-open-in-new', modal: 'post_to_url' },
  webhook_post_modal: { icon: 'mdi-link', modal: 'post_to_url' },
  simple_url: { icon: 'mdi-open-in-new', modal: 'open_url' },
  request_forms: { icon: 'mdi-information-outline', modal: 'Request Forms' },
  simple_url_modal: { icon: 'mdi-link', modal: 'open_url' },
  download_quotes_pdf: { icon: 'mdi-file-download-outline', modal: 'Download Quote PDF' },
  send_quote_as_email: { icon: 'mdi-email-outline', modal: 'Email Quote PDF' },
  send_quote_as_text: { icon: 'mdi-forum-outline', modal: 'Text Quote PDF' },
  firelight_illustration: { icon: 'mdi-chart-box-plus-outline', modal: 'firelight_illustration' },
  firelight_e_app: { icon: 'mdi-file-document-edit-outline', modal: 'firelight_eapp' },
  firelight_e_ticket: { icon: 'mdi-file-document-outline', modal: 'firelight_eticket' }
};

const QuoteActionModal = ({ actions = [], onClose, quote = {}, quoteData = {} }) => {
  const context = useContext(ProductContext);
  const [modal, setModal] = useState('actions');
  const [action, setAction] = useState(null);
  const [hoveringId, setHoveringId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [firelightEapp, setFirelightEapp] = useState(null);

  const to = '';
  const phone_number = '';
  const subject = '';
  const message = '';

  const styles = useMemo(() => baseStyles, []);

  const quoteActions = useMemo(() => {
    const carrierId = quote.carrier_id;
    const productId = quote.product_id;
    return (actions || []).filter(a => {
      const carrierIds = a?.carrier_ids || [];
      const productIds = a?.product_ids || [];
      return carrierIds.includes(carrierId) && productIds.includes(productId);
    });
  }, [actions, quote]);

  const onActionClick = (nextModal, nextAction) => {
    setAction(nextAction);
    setModal(nextModal);
  };

  const handleFireLightQuoteAction = async onClick => {
    const client = quoteData?.client || {};
    const { date_of_birth, current_age, nearest_age } = client;

    let ages_from_dob = {};
    if (date_of_birth) {
      ages_from_dob = FormUtils._getAgesFromDob(date_of_birth);
    }

    setLoading(true);
    try {
      const calculated_current_age = date_of_birth ? ages_from_dob.calculated_current_age : current_age;
      const calculated_nearest_age = date_of_birth ? ages_from_dob.calculated_nearest_age : nearest_age;

      const [pdfResponse, productResponse] = await Promise.all([generateQuotePdf(quote), AaqApi.loadProduct({ id: quote?.product_id })]);
      onClick({
        agent: context?.agent,
        client: context?.client,
        quote: { ...quote, gender: context?.client?.gender, pdf_url: pdfResponse?.data?.url, product_cusip: productResponse[0]?.cusip },
        quoted_config: {
          ...quoteData,
          date_of_birth: date_of_birth ? Moment(date_of_birth).format('YYYY-MM-DD') : null,
          current_age: calculated_current_age,
          nearest_age: calculated_nearest_age
        }
      });
      setLoading(false);
      onClose && onClose();
    } catch (error) {
      console.error('Error handling FireLight quote action:', error);
      setLoading(false);
    }
  };

  const generateQuotePdf = quote => {
    const agent_email = context?.agent?.email || '';
    const agent_license_number = context?.agent?.insurance_license_number || '';
    const agent_phone_number = context?.agent?.phone || '';
    const client_name = `${context?.client?.first_name || ''} ${context?.client?.last_name || ''}`.trim();

    const payload = {
      quote_guids: [quote.id || ''],
      template: 'default',
      pdf_action: 'download',
      show_agent_info: true,
      agent_email,
      from: agent_email,
      agent_first_name: context?.agent?.first_name || '',
      agent_last_name: context?.agent?.last_name || '',
      agent_license: agent_license_number,
      agent_phone: agent_phone_number,
      to,
      client_name,
      phone_number,
      subject,
      message
    };

    return AnnuitiesApi.generateQuotesPdf(payload);
  };

  const fireLightActionIsValid = quote_action => {
    if (FL_DISABLED_ACTION_TYPES.includes(quote_action?.action_type)) return false;
    return true;
  };

  if (modal === 'post_to_url') {
    return <PostToUrlModal data={{ quote, ...quoteData }} errorMsg={action?.error_msg} onClose={() => setModal(null)} successMsg={action?.success_msg} url={action?.url} />;
  }

  if (modal === 'open_url') {
    return <OpenUrlModal data={action?.action_type === 'simple_url' ? null : { quote, ...quoteData }} inline={action?.open_in_modal} onClose={() => setModal(null)} url={action?.url} />;
  }

  if (modal === 'firelight_eapp') {
    if (action?.action_data?.activity === 'e_app') {
      return <FirelightEappModal action={action} e_app={firelightEapp} onClose={() => setModal(null)} onSave={setFirelightEapp} quote={quote} />;
    }
    return <Loader />;
  }

  if (modal === 'firelight_eticket') {
    return <FirelightEticketModal action={action} onClose={() => setModal(null)} onSave={() => setModal(null)} quote={quote} />;
  }

  if (modal === 'Request Forms') {
    return <RequestFormsModal onBackClick={() => setModal(null)} onClose={() => setModal(null)} quote={quote} />;
  }

  if (modal === 'Email Quote PDF') {
    return (
      <PdfActions
        agent={context?.agent || {}}
        client={context?.client || {}}
        solve={context?.solve || ''}
        onClose={onClose}
        selectedQuotes={quote}
        mode='action'
        type='email'
        isCalifornia={quote?.state === 'CA'}
      />
    );
  }

  if (modal === 'Text Quote PDF') {
    return (
      <PdfActions
        agent={context?.agent || {}}
        client={context?.client || {}}
        solve={context?.solve || ''}
        onClose={onClose}
        selectedQuotes={quote}
        mode='action'
        type='text'
        isCalifornia={quote?.state === 'CA'}
      />
    );
  }

  if (modal === 'Download Quote PDF') {
    return (
      <PdfActions
        agent={context?.agent || {}}
        client={context?.client || {}}
        solve={context?.solve || ''}
        onClose={onClose}
        selectedQuotes={quote}
        mode='action'
        type='download'
        isCalifornia={quote?.state === 'CA'}
      />
    );
  }

  const embedActions = context?.aaq?.embed_config_quote_actions || [];

  return (
    <Modal onClose={onClose} title='Select an Action'>
      {loading ? (
        <Loader />
      ) : (
        <Content>
          {embedActions.map((ea, index) => {
            const enabled = ea?.enabled_quote_guids?.includes(quote?.guid);
            if (!enabled) return null;
            const key = ea?.id ?? index;
            const isHover = hoveringId === key;
            const clickHandler = QUOTER_CONFIG?.embed_context === 'fire_light' ? () => handleFireLightQuoteAction(ea.on_click) : () => ea.on_click?.(quote);
            return (
              <div
                key={key}
                onClick={clickHandler}
                onMouseEnter={() => setHoveringId(key)}
                onMouseLeave={() => setHoveringId(null)}
                style={{ ...styles.box_button, ...(isHover ? styles.box_button_selected : {}) }}
              >
                <i className={`mdi mdi-${ea.icon}`} style={styles.box_button_icon} />
                <div style={styles.box_button_label}>{ea.label}</div>
              </div>
            );
          })}

          {quoteActions.map((qa, index) => {
            const type = `${qa?.action_type}${qa?.open_in_modal ? '_modal' : ''}${qa?.action_data?.activity ? `_${qa?.action_data?.activity}` : ''}`;
            const data = META_DATA[type] || META_DATA.default;
            const key = qa?.id ?? index;
            const isHover = hoveringId === key;
            if (QUOTER_CONFIG?.embed_context === 'fire_light' && !fireLightActionIsValid(qa)) return null;

            return (
              <div
                key={key}
                onClick={() => onActionClick(data.modal, qa)}
                onMouseEnter={() => setHoveringId(key)}
                onMouseLeave={() => setHoveringId(null)}
                style={{ ...styles.box_button, ...(isHover ? styles.box_button_selected : {}) }}
                id={`${qa?.label || 'action'}_${index}`}
              >
                {qa?.icon?.url ? <div style={{ ...styles.box_button_image, backgroundImage: `url(${qa.icon.url})` }} /> : <i className={`mdi ${data.icon}`} style={styles.box_button_icon} />}
                <div style={styles.box_button_label}>{qa?.label || type}</div>
              </div>
            );
          })}
        </Content>
      )}
    </Modal>
  );
};

QuoteActionModal.propTypes = {
  actions: PropTypes.array,
  onClose: PropTypes.func,
  quote: PropTypes.object,
  quoteData: PropTypes.object
};

export default QuoteActionModal;
